import React, { useState, useEffect } from 'react';
import { CardComponentProps } from '@/lib/cards';
import {
  getMailFolders,
  subscribe,
  unsubscribe,
  GraphWebhookSubscription,
} from '@/api/mailbox';

/**
 * Graph Webhooks Card
 * Manages Microsoft Graph webhook subscriptions
 */
export const GraphWebhooksCard: React.FC<CardComponentProps> = ({
  card,
  userContext,
  onError,
}) => {
  const [subscriptions, setSubscriptions] = useState<GraphWebhookSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);

  async function loadData() {
    try {
      setLoading(true);
      const result = await getMailFolders();
      setSubscriptions(result.subscriptions);
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, [onError]);

  const handleAction = async (action: string) => {
    try {
      setActionInProgress(true);
      if (action === 'Delete webhooks') {
        await unsubscribe();
      } else {
        // 'Activate all webhooks' and 'Renew subscriptions' both call subscribe
        await subscribe();
      }
      await loadData();
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setActionInProgress(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '1rem', color: '#666' }}>Loading webhooks...</div>;
  }

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <strong>Active Webhooks:</strong> {subscriptions.filter((s) => s.active).length}
          {' / '}
          {subscriptions.length} total
        </div>

        {subscriptions.length > 0 && (
          <ul style={{ marginTop: '0.5rem', paddingLeft: '1.25rem', fontSize: '0.85rem' }}>
            {subscriptions.map((sub) => (
              <li key={sub.id} style={{ marginBottom: '0.25rem', color: sub.active ? '#333' : '#999' }}>
                <span>{sub.resource}</span>
                {' — '}
                <span style={{ color: sub.active ? '#28a745' : '#dc3545' }}>
                  {sub.active ? 'active' : 'inactive'}
                </span>
                {sub.expiryAt && (
                  <span style={{ color: '#999', marginLeft: '0.5rem' }}>
                    (expires {new Date(sub.expiryAt).toLocaleDateString()})
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        {subscriptions.length === 0 && (
          <div style={{ fontSize: '0.9rem', color: '#666' }}>No subscriptions found.</div>
        )}
      </div>

      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {card.cardActionsList.map((action: string) => (
          <button
            key={action}
            onClick={() => handleAction(action)}
            disabled={actionInProgress}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: action === 'Delete webhooks' ? '#dc3545' : '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: actionInProgress ? 'not-allowed' : 'pointer',
              opacity: actionInProgress ? 0.6 : 1,
            }}
          >
            {action}
          </button>
        ))}
      </div>
    </div>
  );
};

GraphWebhooksCard.displayName = 'GraphWebhooksCard';

export default GraphWebhooksCard;
